import React from "react";
import { Table, Tag, Popconfirm, message } from "antd";
import type { ColumnType } from "antd/es/table";
import { Trash2, Send, FileText, History, Briefcase, CheckCircle } from "lucide-react";
import dayjs from "dayjs";
import MyPagination from "../../../views/components/Pagination";
import { IQuote, TQuoteStatus } from "../../../types/api/quote";
import { QData } from "../../../types/basic-type-list";
import { QUOTE_STATUS_CFG as STATUS_CFG } from "../../../constants/statusColors";

interface QuotesTableProps {
  data: IQuote[];
  loading: boolean;
  qData: QData;
  refetch: (params: Partial<QData>) => void;
  DEFAULT_NUQS_CONFIG?: any;
  total: number;
  onEdit: (quote: IQuote) => void;
  onSend: (quote: IQuote) => void;
  onDelete: (id: string) => void;
  onHistory?: (quote: IQuote) => void;
  onConvertToJob?: (quote: IQuote) => void;
  onAcceptOnBehalf?: (quote: IQuote) => void;
}

function fmt(n: number | string | undefined | null) {
  return Number(n ?? 0).toLocaleString("en-GB", { style: "currency", currency: "GBP" });
}

const QuotesTable: React.FC<QuotesTableProps> = ({
  data,
  loading,
  qData,
  refetch,
  DEFAULT_NUQS_CONFIG,
  total,
  onEdit,
  onSend,
  onDelete,
  onHistory,
  onConvertToJob,
  onAcceptOnBehalf,
}) => {
  const columns: ColumnType<IQuote>[] = [
    {
      title: "Quote Ref",
      dataIndex: "quote_number",
      key: "quote_number",
      render: (v) => <span className="font-mono text-xs text-blue-600 font-semibold">{v}</span>,
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (v) => <span className="font-medium text-slate-800 text-sm whitespace-nowrap">{v ?? "—"}</span>,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (v) => <span className="text-slate-600 text-sm truncate max-w-[180px] inline-block">{v}</span>,
    },
    {
      title: "Issued",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (v) => <span className="text-slate-500 text-xs whitespace-nowrap">{v ? dayjs(v).format("DD MMM YYYY") : "—"}</span>,
    },
    {
      title: "Expires",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (v, row) => {
        if (!v) return <span className="text-slate-500 text-xs whitespace-nowrap">—</span>;
        const isExpired =
          dayjs(v).isBefore(dayjs(), "day") &&
          !["accepted", "rejected", "converted"].includes(row.status);
        return (
          <span className={`text-xs whitespace-nowrap ${isExpired ? "text-red-500 font-medium" : "text-slate-500"}`}>
            {dayjs(v).format("DD MMM YYYY")}
            {isExpired && <span className="ml-1 text-[10px] text-red-400">(expired)</span>}
          </span>
        );
      },
    },
    {
      title: "Subtotal",
      dataIndex: "subtotal",
      key: "subtotal",
      align: "right",
      render: (v) => <span className="text-slate-600 tabular-nums">{fmt(v)}</span>,
    },
    {
      title: "VAT",
      dataIndex: "vat_amount",
      key: "vat_amount",
      align: "right",
      render: (v) => <span className="text-slate-500 text-xs tabular-nums">{fmt(v)}</span>,
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (v) => <span className="font-bold text-slate-800 tabular-nums">{fmt(v)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: TQuoteStatus) => {
        const { cls, label } = STATUS_CFG[v] || STATUS_CFG.draft;
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cls}`}>
            {label}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, q) => {
        // `status` only flips to "expired" lazily when the record is next
        // read on the server, so a quote past its expiry_date can still show
        // status "sent"/"viewed" here. Check the date directly so we don't
        // offer an accept action the server will reject.
        const isExpired = Boolean(q.expiry_date) && dayjs(q.expiry_date).isBefore(dayjs(), "day");
        const canSend = q.status === "draft" || q.status === "viewed" || q.status === "sent";
        const canEdit = q.status === "draft";
        const canConvert = q.status === "accepted";
        const canAcceptOnBehalf = (q.status === "sent" || q.status === "viewed") && !isExpired;

        return (
          <div className="flex items-center justify-end gap-1.5">
            {canAcceptOnBehalf && onAcceptOnBehalf && (
              <Popconfirm
                title={`Accept "${q.quote_number}" on behalf of client?`}
                description="This will mark the quote as accepted. The client will not be notified."
                okText="Accept"
                cancelText="Cancel"
                onConfirm={() => onAcceptOnBehalf(q)}
              >
                <button
                  className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center hover:bg-green-100 transition-colors"
                  title="Accept on behalf of client"
                >
                  <CheckCircle size={13} className="text-green-600" />
                </button>
              </Popconfirm>
            )}
            {canConvert && onConvertToJob && (
              <Popconfirm
                title={`Convert "${q.quote_number}" to a job?`}
                description="A new draft job will be created from this quote."
                okText="Convert"
                cancelText="Cancel"
                onConfirm={() => onConvertToJob(q)}
              >
                <button
                  className="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center hover:bg-emerald-100 transition-colors"
                  title="Convert to Job"
                >
                  <Briefcase size={13} className="text-emerald-600" />
                </button>
              </Popconfirm>
            )}
            {q.pdf_url && (
              <a
                href={q.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors"
                title="View PDF"
              >
                <FileText size={13} className="text-red-600" />
              </a>
            )}
            {(q as any).public_token && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/quote/${(q as any).public_token}`);
                  message.success("Link copied!");
                }}
                className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center hover:bg-indigo-100 transition-colors"
                title="Copy URL"
              >
                <FileText size={13} className="text-indigo-600" />
              </button>
            )}
            {canSend && (
              <button
                onClick={() => onSend(q)}
                className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center hover:bg-blue-100 transition-colors"
                title="Send to client"
              >
                <Send size={13} className="text-blue-600" />
              </button>
            )}
            {canEdit && (
              <button
                onClick={() => onEdit(q)}
                className="w-7 h-7 bg-slate-50 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                title="Edit"
              >
                <FileText size={13} className="text-slate-500" />
              </button>
            )}
            {q.status === "draft" && (
              <Popconfirm
                title="Delete this quote?"
                okText="Delete"
                okType="danger"
                onConfirm={() => onDelete(String(q.id))}
              >
                <button
                  className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center hover:bg-red-100 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={13} className="text-red-500" />
                </button>
              </Popconfirm>
            )}
            {onHistory && (
              <button
                onClick={() => onHistory(q)}
                className="w-7 h-7 bg-slate-50 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                title="Status History"
              >
                <History size={13} className="text-slate-500" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey={(item) => item.id.toString()}
      pagination={false}
      loading={loading}
      scroll={{ x: "max-content" }}
      className="custom-table-class"
      footer={() => (
        <MyPagination
          qData={qData}
          onChange={refetch}
          total={total}
          DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
        />
      )}
    />
  );
};

export default QuotesTable;
